import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import s3Service from '../services/s3Service';
import styles from './S3DocumentUpload.module.css';

const S3DocumentUpload = ({
  folder = 'documents',
  onUploadSuccess,
  accept = '.pdf,.doc,.docx',
  maxSize = 10 * 1024 * 1024, // 10MB
  helpText = 'Upload file',
  showPreview = false,
  onPreviewChange = null,
  uploadedFile = null, // Track if file is already uploaded
  onRemoveFile = null, // Callback to remove uploaded file
}) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Generate unique ID for this component instance
  const uniqueId = `document-upload-${Date.now()}-${Math.random()
    .toString(36)
    .substr(2, 9)}`;

  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset preview when selectedFile changes
  useEffect(() => {
    if (!selectedFile && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);

      // Only call preview callback to clear preview if this is a profile picture upload
      // AND if we're not just clearing due to successful upload
      if (
        onPreviewChange &&
        (folder.includes('profile') ||
          folder.includes('guardian-profile-images') ||
          folder.includes('uploads/images/users')) &&
        !uploading // Don't clear preview during upload
      ) {
        onPreviewChange(null);
      }
    }
  }, [selectedFile, previewUrl, onPreviewChange, folder, uploading]);

  // ✅ NEW: Load signed URL for already uploaded file (profile picture preview)
  useEffect(() => {
    const loadUploadedFilePreview = async () => {
      // Check if we have an uploaded file with a key
      if (!uploadedFile || !uploadedFile.key || !showPreview) {
        console.log('[S3 PREVIEW] Skipping preview load:', { uploadedFile, showPreview });
        return;
      }

      try {
        console.log('[S3 PREVIEW] Loading signed URL for:', uploadedFile.key);
        const signedUrl = await s3Service.getFileUrlFromBackend(uploadedFile.key);
        console.log('[S3 PREVIEW] Got signed URL:', signedUrl);

        if (signedUrl) {
          console.log('[S3 PREVIEW] Setting preview URL in component state');
          setPreviewUrl(signedUrl);

          // Notify parent component about preview URL (always when showPreview is true)
          if (onPreviewChange) {
            console.log('[S3 PREVIEW] Calling onPreviewChange callback with URL:', signedUrl);
            onPreviewChange(signedUrl);
          } else {
            console.warn('[S3 PREVIEW] onPreviewChange callback is not defined!');
          }
        } else {
          console.warn('[S3 PREVIEW] Got empty/null signed URL from backend');
        }
      } catch (error) {
        console.error('[S3 PREVIEW] Error loading uploaded file preview:', error);
      }
    };

    loadUploadedFilePreview();
  }, [uploadedFile, showPreview]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size first
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    // Validate file type based on accept prop
    const allowedTypes = accept
      .split(',')
      .map((type) => type.trim().toLowerCase());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const fileMimeType = file.type.toLowerCase();

    let isValidType = false;

    // Check if file type matches allowed types
    for (const allowedType of allowedTypes) {
      if (allowedType.startsWith('.')) {
        // Extension-based validation
        if (fileExtension === allowedType) {
          isValidType = true;
          break;
        }
      } else if (allowedType.includes('/')) {
        // MIME type validation
        if (fileMimeType === allowedType) {
          isValidType = true;
          break;
        }
      }
    }

    if (!isValidType) {
      toast.error(`Invalid file type. Allowed types: ${accept}`);
      return;
    }

    // Create preview URL for images if showPreview is enabled
    if (showPreview && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Only call preview callback if this is a profile picture upload
      if (
        onPreviewChange &&
        (folder.includes('profile') ||
          folder.includes('guardian-profile-images') ||
          folder.includes('uploads/images/users'))
      ) {
        onPreviewChange(url);
      }
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let s3Key;

      // Use appropriate upload method based on file type
      if (selectedFile.type.startsWith('image/')) {
        // Use image upload for images (Profile Picture)
        s3Key = await s3Service.uploadImage(selectedFile, {
          maxSize: maxSize,
          allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
          folder: folder,
          onProgress: (progress) => {
            setUploadProgress(progress);
          },
        });
      } else {
        // Use document upload for documents
        s3Key = await s3Service.uploadDocument(selectedFile, {
          maxSize: maxSize,
          allowedTypes: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          ],
          folder: folder,
          onProgress: (progress) => {
            setUploadProgress(progress);
          },
        });
      }

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(s3Key, selectedFile.name);
      }

      toast.success('File uploaded successfully!');
      setSelectedFile(null);

      // Check if this is a profile picture upload
      const isProfilePicture = folder.includes('profile') ||
        folder.includes('guardian-profile-images') ||
        folder.includes('uploads/images/users');

      // For profile pictures, fetch signed URL and keep the preview
      if (isProfilePicture && showPreview) {
        try {
          console.log('[S3 UPLOAD] Fetching signed URL for uploaded profile picture:', s3Key);
          const signedUrl = await s3Service.getFileUrlFromBackend(s3Key);

          if (signedUrl) {
            // Revoke old blob URL
            if (previewUrl) {
              URL.revokeObjectURL(previewUrl);
            }

            // Set new preview to signed URL
            setPreviewUrl(signedUrl);

            // Update parent component with signed URL
            if (onPreviewChange) {
              onPreviewChange(signedUrl);
            }
            console.log('[S3 UPLOAD] Profile picture preview updated with signed URL');
          }
        } catch (error) {
          console.error('[S3 UPLOAD] Error fetching signed URL for profile picture:', error);
          // Clear preview on error
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }
      } else {
        // For non-profile documents, clear preview after upload
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }

      // Reset file input
      const fileInput = document.getElementById(uniqueId);
      if (fileInput) {
        fileInput.value = '';
      }

      // Reset progress
      setUploadProgress(0);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'webp':
      case 'gif':
        return '🖼️';
      default:
        return '📄';
    }
  };

  return (
    <div className={styles.documentUploadContainer}>
      {/* Show uploaded file if exists */}
      {uploadedFile ? (
        <div className={styles.uploadedFile}>
          <div className={styles.fileInfo}>
            <span className={styles.fileIcon}>
              {getFileIcon(uploadedFile.name || 'uploaded')}
            </span>
            <span className={styles.fileName}>
              {uploadedFile.name || 'File uploaded'}
            </span>
            <span className={styles.uploadedStatus}>✓ Uploaded</span>
          </div>
          {onRemoveFile && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDanger} ${styles.btnSm}`}
              onClick={() => onRemoveFile()}
            >
              Remove
            </button>
          )}
        </div>
      ) : (
        /* Show upload interface if no file uploaded */
        <>
          {/* Show preview if enabled and previewUrl exists */}
          {showPreview && previewUrl && (
            <div className={styles.previewContainer}>
              <img
                src={previewUrl}
                alt="File preview"
                className={styles.previewImage}
              />
            </div>
          )}
          
          <div className={styles.formGroup}>
            <input
              id={uniqueId}
              type="file"
              className={styles.formControl}
              accept={accept}
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <label htmlFor={uniqueId} className={styles.formControl}>
              {helpText}
            </label>
          </div>

          {selectedFile && (
            <div className={styles.selectedFile}>
              <div className={styles.fileInfo}>
                <span className={styles.fileIcon}>
                  {getFileIcon(selectedFile.name)}
                </span>
                <span className={styles.fileName}>{selectedFile.name}</span>
                <span className={styles.fileSize}>
                  ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                </span>
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className={styles.progressContainer}>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span className={styles.progressText}>
                    {Math.round(uploadProgress)}%
                  </span>
                </div>
              )}

              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span
                      className={`${styles.spinnerBorder} ${styles.spinnerBorderSm} ${styles.me2}`}
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Uploading...
                  </>
                ) : (
                  'Upload File'
                )}
              </button>
            </div>
          )}

          <small className={styles.formText}>
            Max size: {maxSize / (1024 * 1024)}MB
          </small>
        </>
      )}
    </div>
  );
};

export default S3DocumentUpload;
